import { useState } from 'react';
import { useLocation } from 'wouter';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  useCustomerCreate,
  CUSTOMER_STATUS_LIST,
  CUSTOMER_STATUS_LABELS,
  INTENT_LEVEL_LABELS,
  type CustomerStatus,
  type IntentLevel,
} from '@/hooks/useCrm';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { CRM_PERMISSIONS, hasPermission } from '@/lib/crmPermissions';

export default function CustomerCreatePage() {
  // ── All hooks must be called unconditionally at the top level ──────────────
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { loading, error, create } = useCustomerCreate();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<CustomerStatus>('lead');
  const [intentLevel, setIntentLevel] = useState<IntentLevel | ''>('');
  const [channelPartnerId, setChannelPartnerId] = useState('');

  // Derive permission flag after all hooks are called.
  const canCreate = hasPermission(permissions, CRM_PERMISSIONS.CUSTOMER_CREATE);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard mutation: reject if permission was revoked between renders.
    if (!canCreate) {
      toast.error('权限不足，无法创建客户');
      return;
    }
    if (!name.trim()) {
      toast.error('姓名为必填项');
      return;
    }
    const result = await create({
      name: name.trim(),
      phone: phone.trim() || undefined,
      email: email.trim() || undefined,
      status,
      intentLevel: intentLevel !== '' ? intentLevel : undefined,
      channelPartnerId: channelPartnerId.trim() || undefined,
    });
    if (result) {
      toast.success('客户创建成功');
      navigate(`/crm/customers/${result.id}`);
    } else if (error) {
      toast.error(`创建失败：${error.message}`);
    }
  };

  // ── Permission early return — after all hooks ─────────────────────────────
  if (!canCreate) {
    return <AccessDenied resource="新建客户" />;
  }

  return (
    <div className="mx-auto max-w-lg space-y-4">
      <h2 className="text-xl font-semibold">新建客户</h2>
      <Card className="p-6">
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="name">姓名 *</Label>
            <Input
              id="name"
              data-testid="customer-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="请输入客户姓名"
              required
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="phone">手机号</Label>
            <Input
              id="phone"
              data-testid="customer-phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="可选"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="email">邮箱</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="可选"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="status">初始阶段</Label>
            <select
              id="status"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as CustomerStatus)}
            >
              {CUSTOMER_STATUS_LIST.map((s) => (
                <option key={s} value={s}>
                  {CUSTOMER_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="intentLevel">意向等级</Label>
            <select
              id="intentLevel"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={intentLevel}
              onChange={(e) => setIntentLevel(e.target.value as IntentLevel | '')}
            >
              <option value="">不设置</option>
              {(['high', 'medium', 'low', 'none'] as IntentLevel[]).map((l) => (
                <option key={l} value={l}>
                  {INTENT_LEVEL_LABELS[l]}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="channelPartnerId">渠道合作方 ID</Label>
            <Input
              id="channelPartnerId"
              value={channelPartnerId}
              onChange={(e) => setChannelPartnerId(e.target.value)}
              placeholder="可选"
            />
          </div>

          {error && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error.status === 409 ? '数据冲突，请刷新后重试' : error.message}
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={loading}>
              {loading ? '提交中…' : '创建'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/crm/customers')}
            >
              取消
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
